import { RPC_ORDER_ENDPOINTS } from "../endpoints";
import { buildPlaceOrderPayload } from "../payload";
import postgreApiService from "./apiService";
import { getStoredCompanyId, getStoredUserId } from "../../utils/user";
import { isSuccess } from "./utils";

const normalizeStatus = (value) => {
    return String(value || "processing").toLowerCase();
};

const isValidImageSource = (value) => {
    const source = String(value || "").trim();
    if (!source) return false;
    return (
        source.startsWith("http://") ||
        source.startsWith("https://") ||
        source.startsWith("/") ||
        source.startsWith("data:image/")
    );
};

const normalizeOrderListItem = (item) => {
    const statusKey = normalizeStatus(item?.order_status || item?.Status || item?.status);
    const rawCost = item?.finalamount ?? item?.TotalAmount ?? item?.total_amount ?? item?.Cost ?? item?.cost ?? 0;
    const cost = typeof rawCost === "number" ? `€${rawCost.toFixed(2)}` : String(rawCost || "€0.00");
    const rawId = item?.orderid ?? item?.OrderID ?? item?.order_id ?? item?.Id ?? item?.id ?? "";
    const seriesDescription = (item?.seriesdescription || item?.SeriesDescription || "").trim();
    return {
        id: `#${rawId}`,
        displayId: seriesDescription ? `#${seriesDescription}` : `#${rawId}`,
        date: item?.orderdate || item?.OrderDate || item?.order_date || item?.CreatedAt || item?.created_at || "",
        gifts: item?.totalqty ?? item?.TotalPieces ?? item?.total_pieces ?? item?.ItemCount ?? item?.item_count ?? 0,
        status: statusKey.charAt(0).toUpperCase() + statusKey.slice(1),
        statusKey,
        cost,
    };
};

const normalizeOrderDetailItem = (item) => {
    const productId = item?.productid ?? item?.ProductID ?? item?.product_id ?? item?.Id ?? item?.id ?? "";
    const rawPrice = item?.initprice ?? item?.InitPrice ?? item?.init_price ?? item?.priceamount ?? item?.Price ?? item?.price ?? 0;
    const rawImage = item?.ProductImage || item?.product_image || item?.Image || item?.image;
    return {
        id: String(productId),
        name: item?.ProductName || item?.product_name || item?.Name || item?.name || (productId ? `Product #${productId}` : "Unnamed product"),
        code: String(item?.ProductCode ?? item?.product_code ?? item?.Code ?? item?.code ?? ""),
        qty: item?.qty ?? item?.Quantity ?? item?.quantity ?? item?.Qty ?? 1,
        price: typeof rawPrice === "number" ? `€${rawPrice.toFixed(2)}` : String(rawPrice || "€0.00"),
        image: isValidImageSource(rawImage) ? rawImage : "/assets/icons/korres-shares-logo.png",
        link: item?.ReferanceID3 || item?.referanceID3 || item?.link || item?.Link || "",
    };
};

export const getOrderSeriesList = async () => {
    const p_company_id = getStoredCompanyId();
    const p_user_id = getStoredUserId();
    const response = await postgreApiService.post(RPC_ORDER_ENDPOINTS.GET_ORDER_SERIES_LIST, {
        p_company_id,
        p_user_id,
    });
    return response?.data || null;
};

export const placeOrder = async (payload) => {
    const response = await postgreApiService.post(RPC_ORDER_ENDPOINTS.INSERT_CUSTOMER_ORDER_DETAILS, buildPlaceOrderPayload(payload));

    const data = response?.data || {};
    const success = isSuccess(data?.Success);
    if (!success) {
        throw new Error(data?.Message || data?.msg || "Order failed.");
    }

    return data;
};

export const getOrderList = async () => {
    const company_id = getStoredCompanyId();
    const user_id = getStoredUserId();
    const response = await postgreApiService.post(RPC_ORDER_ENDPOINTS.GET_ORDER_LIST, { company_id, user_id });
    const raw = response?.data;

    // Response may be a direct array
    if (Array.isArray(raw)) {
        return {
            success: true,
            orders: raw.map(normalizeOrderListItem),
            error: undefined,
        };
    }

    const data = raw || {};
    const list = data?.Data || data?.data || data?.Orders || data?.orders || data;
    const orders = Array.isArray(list) ? list.map(normalizeOrderListItem) : [];
    return {
        success: isSuccess(data?.Success),
        orders,
        error: !isSuccess(data?.Success) ? (data?.Message || "") : undefined,
    };
};

export const getOrderDetails = async (orderId) => {
    const company_id = getStoredCompanyId();
    const response = await postgreApiService.post(RPC_ORDER_ENDPOINTS.GET_ORDER_DETAILS, {
        json_data: { company_id, order_id: Number(orderId) },
    });
    const data = response?.data || {};
    const statusRaw = data?.Status || data?.status || "";
    const statusKey = normalizeStatus(statusRaw);
    const rawItems = data?.Items || data?.items || [];
    const items = rawItems.map((item) => {
        const rawImage = item?.ProductImage || item?.product_image;
        const rawPrice = item?.productprice ?? item?.ProductPrice ?? item?.price ?? 0;
        return {
            id: String(item?.ProductID ?? item?.product_id ?? ""),
            name: item?.ProductName || item?.product_name || "",
            code: String(item?.ProductCode ?? item?.product_code ?? ""),
            qty: item?.Quantity ?? item?.quantity ?? 1,
            price: `€${Number(rawPrice).toFixed(2)}`,
            image: isValidImageSource(rawImage) ? rawImage : "/assets/icons/korres-shares-logo.png",
            link: item?.ReferanceID3 || item?.referanceID3 || item?.link || item?.Link || "",
        };
    });
    const totalAmount = data?.total_amount ?? data?.TotalAmount ?? 0;
    const discount = data?.Discount ?? data?.discount ?? 0;
    const finalAmount = data?.Final_Amount ?? data?.final_amount ?? 0;
    const rawId = data?.OrderID ?? data?.order_id ?? orderId;
    const seriesDescription = String(data?.SeriesDescription || data?.seriesdescription || "").trim();
    return {
        success: isSuccess(data?.Success),
        id: rawId,
        displayId: seriesDescription ? `#${seriesDescription}` : `#${rawId}`,
        status: statusRaw,
        statusKey,
        statusId: data?.StatusID ?? data?.status_id ?? null,
        items,
        totalAmount: `€${Number(totalAmount).toFixed(2)}`,
        discount: `€${Number(discount).toFixed(2)}`,
        finalAmount: `€${Number(finalAmount).toFixed(2)}`,
    };
};

export const getOrderStatusList = async () => {
    const company_id_ = getStoredCompanyId();
    const response = await postgreApiService.post(RPC_ORDER_ENDPOINTS.GET_ORDER_STATUS_LIST, { company_id_ });
    const raw = response?.data;
    const list = Array.isArray(raw) ? raw : (raw?.Data || raw?.data || []);
    return list.map((item) => ({
        id: item?.id ?? item?.Id ?? null,
        title: item?.title || item?.Title || "",
        color: item?.color || item?.Color || "",
    }));
};

export const getOrderDetail = async (orderId) => {
    const company_id = getStoredCompanyId();
    const response = await postgreApiService.post(RPC_ORDER_ENDPOINTS.GET_ORDER_DETAIL, {
        company_id,
        order_id: Number(orderId),
    });
    const raw = response?.data;

    // Response may be a direct array of items
    if (Array.isArray(raw)) {
        const items = raw.map(normalizeOrderDetailItem);
        const firstItem = raw[0] || {};
        return {
            success: true,
            id: firstItem?.orderid ?? firstItem?.OrderID ?? orderId,
            orderDate: "",
            deliveryDate: "",
            status: "Processing",
            statusKey: "processing",
            cost: "€0.00",
            comment: "",
            items,
            error: undefined,
        };
    }

    const data = raw || {};
    const payload = data?.Data || data?.data || data?.Order || data?.order || data || {};
    const statusKey = normalizeStatus(payload?.Status || payload?.status);
    const rawItems = payload?.Items || payload?.items || payload?.OrderItems || payload?.order_items || [];
    const items = Array.isArray(rawItems) ? rawItems.map(normalizeOrderDetailItem) : [];

    return {
        success: isSuccess(data?.Success),
        id: payload?.OrderID || payload?.order_id || payload?.Id || payload?.id,
        orderDate: payload?.OrderDate || payload?.order_date || "",
        deliveryDate: payload?.DeliveryDate || payload?.delivery_date || "",
        status: statusKey.charAt(0).toUpperCase() + statusKey.slice(1),
        statusKey,
        cost: payload?.TotalAmount || payload?.total_amount || payload?.Cost || payload?.cost || "€0.00",
        comment: payload?.Comment || payload?.comment || "",
        items,
        error: !isSuccess(data?.Success) ? (data?.Message || "") : undefined,
    };
};
